import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  Users,
  Activity,
  FileStack,
  Bot,
  DollarSign,
  Mail,
  RefreshCw,
  ShieldCheck,
  ShieldPlus,
  Trash2,
  UserCog,
} from "lucide-react";

import { db, functions } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useAppState } from "../hooks/useAppState";
import LoadingScreen from "../components/common/LoadingScreen";
import UsersTable from "../components/admin/UsersTable";
import AccessRequestsSection from "../components/admin/AccessRequestsSection";
import FeedbackAdminSection from "../components/feedback/FeedbackAdminSection";
import {
  clearCachedUsersData,
  fetchUsersData,
  loadCachedUsersData,
  logAdminAccess,
} from "../services/adminService";

const migrateAllUserRolesFn = httpsCallable(functions, "migrateAllUserRoles");

const STALE_MS = 10 * 60 * 1000; // 10 minuti
const EMAIL_MONTHLY_LIMIT = 200;
const STORAGE_LIMIT_KB = 1_000_000; // 1 GB (riferimento free tier Firestore)
const AVG_DOC_SIZE_KB = 2;

const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n);
const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const fmtAgo = (ts) => {
  if (!ts) return "mai";
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s fa`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.round(m / 60);
  return `${h}h fa`;
};

async function fetchAdminMetrics() {
  const now = new Date();
  const sevenDaysAgo = Timestamp.fromDate(
    new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  );
  const thirtyDaysAgo = Timestamp.fromDate(
    new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  );
  const monthStart = Timestamp.fromDate(
    new Date(now.getFullYear(), now.getMonth(), 1)
  );

  const usersTotalSnap = await getCountFromServer(collection(db, "users"));
  const usersActiveSnap = await getCountFromServer(
    query(collection(db, "users"), where("lastLoginAt", ">=", sevenDaysAgo))
  );

  const [invoicesSnap, clientsSnap, timesheetsSnap, f24Snap, pensionSnap] =
    await Promise.all([
      getCountFromServer(collectionGroup(db, "invoices")),
      getCountFromServer(collectionGroup(db, "clients")),
      getCountFromServer(collectionGroup(db, "timesheets")),
      getCountFromServer(collectionGroup(db, "f24")),
      getCountFromServer(collectionGroup(db, "pensionContributions")),
    ]);
  const documentsTotal =
    invoicesSnap.data().count +
    clientsSnap.data().count +
    timesheetsSnap.data().count +
    f24Snap.data().count +
    pensionSnap.data().count;

  const olliCountSnap = await getCountFromServer(collection(db, "olliLogs"));

  // Costo Olli: sommo client-side i doc dell'ultimo mese (bound di lettura).
  const olliRecentQ = query(
    collection(db, "olliLogs"),
    where("timestamp", ">=", monthStart)
  );
  const olliRecent = await getDocs(olliRecentQ);
  let costUSDMonth = 0;
  olliRecent.forEach((doc) => {
    const d = doc.data();
    costUSDMonth += d.estimatedCostUSD || 0;
  });

  // Email inviate nel mese corrente (tutti gli utenti). Il campo timestamp
  // in users/{uid}/emailLog è "createdAt" (vedi firestoreService.logEmail).
  const emailMonthQ = query(
    collectionGroup(db, "emailLog"),
    where("createdAt", ">=", monthStart)
  );
  let emailsThisMonth = 0;
  try {
    const emailSnap = await getCountFromServer(emailMonthQ);
    emailsThisMonth = emailSnap.data().count;
  } catch (err) {
    // Fallback: se manca l'indice o il campo, conta tutto emailLog.
    const allEmails = await getCountFromServer(collectionGroup(db, "emailLog"));
    emailsThisMonth = allEmails.data().count;
  }

  // Trend nuovi utenti ultimi 30gg (raggruppati per giorno).
  const newUsersQ = query(
    collection(db, "users"),
    where("createdAt", ">=", thirtyDaysAgo)
  );
  const newUsersSnap = await getDocs(newUsersQ);
  const trendMap = new Map();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    trendMap.set(d.toISOString().slice(0, 10), 0);
  }
  newUsersSnap.forEach((doc) => {
    const d = doc.data();
    const ts = d.createdAt?.toDate ? d.createdAt.toDate() : null;
    if (!ts) return;
    const key = ts.toISOString().slice(0, 10);
    if (trendMap.has(key)) trendMap.set(key, trendMap.get(key) + 1);
  });
  const trend = Array.from(trendMap.entries()).map(([date, count]) => ({
    date,
    count,
  }));

  return {
    usersTotal: usersTotalSnap.data().count,
    usersActive7d: usersActiveSnap.data().count,
    documentsTotal,
    olliRequestsTotal: olliCountSnap.data().count,
    costUSDMonth,
    emailsThisMonth,
    trend,
    fetchedAt: Date.now(),
  };
}

function KpiCard({ icon: Icon, label, value, hint, darkMode }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon
          className={`w-4 h-4 ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}
        />
        <span
          className={`text-xs font-medium ${
            darkMode ? "text-neutral-400" : "text-neutral-500"
          }`}
        >
          {label}
        </span>
      </div>
      <div
        className={`text-2xl font-semibold ${
          darkMode ? "text-neutral-100" : "text-neutral-900"
        }`}
      >
        {value}
      </div>
      {hint && (
        <div
          className={`text-xs mt-1 ${
            darkMode ? "text-neutral-500" : "text-neutral-500"
          }`}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ label, current, max, unit, darkMode }) {
  const pct = Math.min(100, Math.max(0, (current / max) * 100));
  const color =
    pct >= 90
      ? "bg-red-500"
      : pct >= 70
        ? "bg-amber-500"
        : "bg-forfy-500";
  return (
    <div
      className={`rounded-xl border p-4 ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={`text-sm font-medium ${
            darkMode ? "text-neutral-300" : "text-neutral-700"
          }`}
        >
          {label}
        </span>
        <span
          className={`text-xs ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}
        >
          {fmtInt(current)} / {fmtInt(max)} {unit} ({pct.toFixed(1)}%)
        </span>
      </div>
      <div
        className={`h-2 rounded-full overflow-hidden ${
          darkMode ? "bg-neutral-800" : "bg-neutral-100"
        }`}
      >
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function TrendChart({ data, darkMode }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const barColor = darkMode ? "bg-forfy-400" : "bg-forfy-500";
  const total = data.reduce((acc, d) => acc + d.count, 0);

  return (
    <div
      className={`rounded-xl border p-4 ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className={`text-sm font-semibold ${
            darkMode ? "text-neutral-200" : "text-neutral-800"
          }`}
        >
          Nuovi utenti (ultimi 30 giorni)
        </h3>
        <span
          className={`text-xs ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}
        >
          Totale: {fmtInt(total)}
        </span>
      </div>
      <div className="flex items-end gap-[2px] h-32">
        {data.map((d) => {
          const h = (d.count / max) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col justify-end"
              title={`${d.date}: ${d.count}`}
            >
              <div
                className={`${barColor} rounded-sm transition-all`}
                style={{ height: `${h}%`, minHeight: d.count > 0 ? "2px" : 0 }}
              />
            </div>
          );
        })}
      </div>
      <div
        className={`flex justify-between text-[10px] mt-1 ${
          darkMode ? "text-neutral-500" : "text-neutral-400"
        }`}
      >
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function fmtDateTs(ts) {
  if (!ts) return "—";
  const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Sezione "Accessi approvati": chi può entrare in Forfy (approvedUsers/{email}).
// Da non confondere con la whitelist qui sopra, che è un'altra cosa: quella dà
// Pro gratis a chi è GIÀ dentro, questa decide chi entra. La chiave è l'email e
// non l'uid perché si approva anche chi non si è ancora registrato.
function AccessiApprovatiSection({ darkMode, adminEmail }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [removingEmail, setRemovingEmail] = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, "approvedUsers")),
      (snap) => {
        setEntries(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => a.id.localeCompare(b.id))
        );
        setLoading(false);
      },
      (err) => {
        console.error("AccessiApprovatiSection snapshot error:", err);
        setError(err?.message || "Errore nel caricamento degli accessi.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleAdd = useCallback(
    async (e) => {
      e.preventDefault();
      setFormMessage(null);
      const email = emailInput.trim().toLowerCase();
      if (!email || !email.includes("@")) {
        setFormMessage({ ok: false, text: "Inserisci un indirizzo email valido." });
        return;
      }
      setSubmitting(true);
      try {
        await setDoc(doc(db, "approvedUsers", email), {
          email,
          source: "manual",
          approvedBy: adminEmail || null,
          approvedAt: serverTimestamp(),
        });
        setFormMessage({ ok: true, text: `${email} può accedere.` });
        setEmailInput("");
      } catch (err) {
        console.error("AccessiApprovatiSection add error:", err);
        setFormMessage({ ok: false, text: err?.message || "Errore durante il salvataggio." });
      } finally {
        setSubmitting(false);
      }
    },
    [emailInput, adminEmail]
  );

  const handleRemove = useCallback(async (email) => {
    if (
      !window.confirm(
        `Revocare l'accesso a ${email}? Al prossimo caricamento vedrà la schermata di attesa e non potrà più salvare dati.`
      )
    )
      return;
    setRemovingEmail(email);
    try {
      await deleteDoc(doc(db, "approvedUsers", email));
    } catch (err) {
      console.error("AccessiApprovatiSection remove error:", err);
      window.alert(err?.message || "Errore durante la revoca.");
    } finally {
      setRemovingEmail(null);
    }
  }, []);

  return (
    <div
      className={`rounded-xl border p-4 mt-4 ${
        darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldPlus className={`w-4 h-4 ${darkMode ? "text-forfy-300" : "text-forfy-600"}`} />
        <h3 className={`text-sm font-semibold ${darkMode ? "text-neutral-200" : "text-neutral-800"}`}>
          Accessi approvati ({entries.length})
        </h3>
      </div>
      <p className={`text-xs mb-4 ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
        Chi non è in questo elenco vede la schermata di attesa e non può salvare nulla.
        Si può approvare un indirizzo anche prima che si registri.
      </p>

      <form onSubmit={handleAdd} className="flex flex-wrap gap-2 mb-4">
        <input
          type="email"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          placeholder="email@esempio.it"
          className={`flex-1 min-w-[200px] min-h-11 rounded-lg border px-3 text-sm ${
            darkMode
              ? "bg-neutral-800 border-neutral-700 text-white"
              : "bg-white border-neutral-300 text-neutral-900"
          }`}
        />
        <button
          type="submit"
          disabled={submitting}
          className="min-h-11 rounded-lg bg-forfy-500 px-4 text-sm font-medium text-white hover:bg-forfy-600 disabled:opacity-50"
        >
          {submitting ? "Salvo…" : "Approva"}
        </button>
      </form>

      {formMessage && (
        <p className={`text-xs mb-3 ${formMessage.ok ? "text-emerald-500" : "text-red-500"}`}>
          {formMessage.text}
        </p>
      )}
      {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
      {loading && <p className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>Caricamento…</p>}

      {!loading && entries.length === 0 && (
        <p className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
          Nessun accesso approvato.
        </p>
      )}

      {!loading && entries.length > 0 && (
        <ul className="space-y-1">
          {entries.map((e) => (
            <li
              key={e.id}
              className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${
                darkMode ? "bg-neutral-800/60" : "bg-neutral-50"
              }`}
            >
              <span className="min-w-0 flex-1 truncate">
                {e.id}
                {e.source && (
                  <span className={`ml-2 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
                    {e.source}
                  </span>
                )}
              </span>
              <button
                onClick={() => handleRemove(e.id)}
                disabled={removingEmail === e.id}
                className="min-h-11 shrink-0 px-2 text-xs text-red-500 hover:underline disabled:opacity-50"
              >
                {removingEmail === e.id ? "Revoco…" : "Revoca"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Sezione "Whitelist": utenti con accesso Pro forzato senza abbonamento attivo
// (bypass gestito lato client in useSubscription tramite whitelist/{email}).
function WhitelistSection({ darkMode, adminEmail }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [emailInput, setEmailInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [expiresInput, setExpiresInput] = useState(""); // yyyy-mm-dd oppure vuoto
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null); // { ok, text }
  const [removingEmail, setRemovingEmail] = useState(null);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "whitelist"), orderBy("addedAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("WhitelistSection snapshot error:", err);
        setError(err?.message || "Errore nel caricamento della whitelist.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleAdd = useCallback(
    async (e) => {
      e.preventDefault();
      setFormMessage(null);
      const email = emailInput.trim().toLowerCase();
      const reason = reasonInput.trim();

      if (!EMAIL_RE.test(email)) {
        setFormMessage({ ok: false, text: "Inserisci un indirizzo email valido." });
        return;
      }

      let expiresAt = null;
      if (expiresInput) {
        const d = new Date(expiresInput);
        if (Number.isNaN(d.getTime())) {
          setFormMessage({ ok: false, text: "Data di scadenza non valida." });
          return;
        }
        expiresAt = Timestamp.fromDate(d);
      }

      setSubmitting(true);
      try {
        await setDoc(doc(db, "whitelist", email), {
          email,
          addedAt: serverTimestamp(),
          addedBy: adminEmail || null,
          reason: reason || null,
          expiresAt,
        });
        setFormMessage({ ok: true, text: `${email} aggiunta alla whitelist.` });
        setEmailInput("");
        setReasonInput("");
        setExpiresInput("");
      } catch (err) {
        console.error("WhitelistSection add error:", err);
        setFormMessage({ ok: false, text: err?.message || "Errore durante il salvataggio." });
      } finally {
        setSubmitting(false);
      }
    },
    [emailInput, reasonInput, expiresInput, adminEmail]
  );

  const handleRemove = useCallback(async (email) => {
    if (!window.confirm(`Rimuovere ${email} dalla whitelist?`)) return;
    setRemovingEmail(email);
    try {
      await deleteDoc(doc(db, "whitelist", email));
    } catch (err) {
      console.error("WhitelistSection remove error:", err);
      window.alert(err?.message || "Errore durante la rimozione.");
    } finally {
      setRemovingEmail(null);
    }
  }, []);

  return (
    <div
      className={`rounded-xl border p-4 mt-4 ${
        darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldPlus
          className={`w-4 h-4 ${darkMode ? "text-forfy-300" : "text-forfy-600"}`}
        />
        <h3
          className={`text-sm font-semibold ${
            darkMode ? "text-neutral-200" : "text-neutral-800"
          }`}
        >
          Whitelist (accesso Pro senza limiti)
        </h3>
      </div>
      <p className={`text-xs mb-4 ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
        Gli utenti in whitelist ottengono accesso Pro forzato indipendentemente
        dall'abbonamento. Lascia vuota la scadenza per un accesso illimitato nel tempo.
      </p>

      {/* Form aggiunta */}
      <form
        onSubmit={handleAdd}
        className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1.2fr_auto] gap-2 mb-4"
      >
        <input
          type="email"
          required
          placeholder="email@esempio.it"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          className={`px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-forfy-400 ${
            darkMode
              ? "bg-neutral-800 border-neutral-700 text-neutral-100 placeholder-neutral-500"
              : "bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400"
          }`}
        />
        <input
          type="text"
          placeholder="Motivo (es. beta tester)"
          value={reasonInput}
          onChange={(e) => setReasonInput(e.target.value)}
          className={`px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-forfy-400 ${
            darkMode
              ? "bg-neutral-800 border-neutral-700 text-neutral-100 placeholder-neutral-500"
              : "bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400"
          }`}
        />
        <input
          type="date"
          value={expiresInput}
          onChange={(e) => setExpiresInput(e.target.value)}
          aria-label="Scadenza (opzionale)"
          className={`px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-forfy-400 ${
            darkMode
              ? "bg-neutral-800 border-neutral-700 text-neutral-100"
              : "bg-white border-neutral-300 text-neutral-900"
          }`}
        />
        <button
          type="submit"
          disabled={submitting}
          className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            submitting
              ? darkMode
                ? "bg-neutral-800 text-neutral-500"
                : "bg-neutral-200 text-neutral-400"
              : darkMode
                ? "bg-forfy-500/15 text-forfy-300 hover:bg-forfy-500/25"
                : "bg-forfy-50 text-forfy-700 hover:bg-forfy-100"
          }`}
        >
          <ShieldPlus className="w-4 h-4" />
          {submitting ? "Aggiungo..." : "Aggiungi"}
        </button>
      </form>

      {formMessage && (
        <div
          className={`mb-4 p-2 rounded-lg text-xs ${
            formMessage.ok
              ? darkMode
                ? "bg-green-900/30 text-green-200"
                : "bg-green-50 text-green-700"
              : darkMode
                ? "bg-red-900/30 text-red-200"
                : "bg-red-50 text-red-700"
          }`}
        >
          {formMessage.text}
        </div>
      )}

      {error && (
        <div
          className={`mb-4 p-2 rounded-lg text-xs ${
            darkMode ? "bg-red-900/30 text-red-200" : "bg-red-50 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      {/* Lista whitelist */}
      {loading ? (
        <div className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
          Caricamento whitelist...
        </div>
      ) : entries.length === 0 ? (
        <div className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
          Nessun utente in whitelist.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                className={`text-left text-xs ${
                  darkMode ? "text-neutral-400" : "text-neutral-500"
                }`}
              >
                <th className="font-medium py-1.5 pr-3">Email</th>
                <th className="font-medium py-1.5 pr-3">Motivo</th>
                <th className="font-medium py-1.5 pr-3">Aggiunta il</th>
                <th className="font-medium py-1.5 pr-3">Da</th>
                <th className="font-medium py-1.5 pr-3">Scadenza</th>
                <th className="font-medium py-1.5 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className={`border-t ${
                    darkMode ? "border-neutral-800" : "border-neutral-100"
                  }`}
                >
                  <td
                    className={`py-2 pr-3 font-medium ${
                      darkMode ? "text-neutral-100" : "text-neutral-900"
                    }`}
                  >
                    {entry.email || entry.id}
                  </td>
                  <td
                    className={`py-2 pr-3 ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    {entry.reason || "—"}
                  </td>
                  <td
                    className={`py-2 pr-3 ${
                      darkMode ? "text-neutral-400" : "text-neutral-500"
                    }`}
                  >
                    {fmtDateTs(entry.addedAt)}
                  </td>
                  <td
                    className={`py-2 pr-3 ${
                      darkMode ? "text-neutral-400" : "text-neutral-500"
                    }`}
                  >
                    {entry.addedBy || "—"}
                  </td>
                  <td
                    className={`py-2 pr-3 ${
                      darkMode ? "text-neutral-400" : "text-neutral-500"
                    }`}
                  >
                    {entry.expiresAt ? fmtDateTs(entry.expiresAt) : "mai"}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <button
                      type="button"
                      disabled={removingEmail === entry.id}
                      onClick={() => handleRemove(entry.id)}
                      aria-label={`Rimuovi ${entry.email || entry.id} dalla whitelist`}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                        darkMode
                          ? "text-red-300 hover:bg-red-900/30"
                          : "text-red-600 hover:bg-red-50"
                      }`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Rimuovi
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { darkMode } = useAppState();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await fetchAdminMetrics();
      setData(m);
    } catch (err) {
      console.error("AdminPage fetch error:", err);
      setError(err?.message || "Errore nel caricamento delle metriche.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async ({ force = false } = {}) => {
    if (!force) {
      const cached = loadCachedUsersData();
      if (cached) {
        setUsersData(cached.data);
        return;
      }
    } else {
      clearCachedUsersData();
    }
    setUsersLoading(true);
    setUsersError(null);
    try {
      const result = await fetchUsersData();
      setUsersData(result.data);
    } catch (err) {
      console.error("AdminPage users fetch error:", err);
      setUsersError(err?.message || "Errore nel caricamento utenti.");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    clearCachedUsersData();
    await Promise.all([load(), loadUsers({ force: true })]);
  }, [load, loadUsers]);

  const runMigration = useCallback(async () => {
    if (migrating) return;
    if (
      !window.confirm(
        "Eseguire la migrazione dei ruoli su TUTTI gli utenti? Gli admin verranno settati in base alla lista server-side."
      )
    ) {
      return;
    }
    setMigrating(true);
    setMigrateResult(null);
    try {
      const res = await migrateAllUserRolesFn();
      setMigrateResult({ ok: true, ...res.data });
      clearCachedUsersData();
      await Promise.all([load(), loadUsers({ force: true })]);
    } catch (err) {
      console.error("migrateAllUserRoles error:", err);
      setMigrateResult({
        ok: false,
        message: err?.message || "Errore migrazione.",
      });
    } finally {
      setMigrating(false);
    }
  }, [migrating, load, loadUsers]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!data || Date.now() - data.fetchedAt > STALE_MS) {
      load();
    }
    loadUsers();
    logAdminAccess(user?.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const storageEstimateKB = useMemo(
    () => (data ? data.documentsTotal * AVG_DOC_SIZE_KB : 0),
    [data]
  );

  if (roleLoading) {
    return <LoadingScreen message="Verifica permessi..." darkMode={darkMode} />;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const freshnessLabel = data
    ? `aggiornato ${fmtAgo(data.fetchedAt)}`
    : "mai aggiornato";

  return (
    <div className={`min-h-screen ${darkMode ? "bg-neutral-950" : "bg-neutral-50"}`}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                darkMode ? "bg-forfy-500/15" : "bg-forfy-50"
              }`}
            >
              <ShieldCheck
                className={`w-5 h-5 ${
                  darkMode ? "text-forfy-300" : "text-forfy-600"
                }`}
              />
            </div>
            <div>
              <h1
                className={`text-xl font-bold ${
                  darkMode ? "text-neutral-100" : "text-neutral-900"
                }`}
              >
                Dashboard Admin
              </h1>
              <p
                className={`text-xs ${
                  darkMode ? "text-neutral-400" : "text-neutral-500"
                }`}
              >
                {freshnessLabel}
              </p>
            </div>
          </div>
          <button
            onClick={refreshAll}
            disabled={loading || usersLoading}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              loading || usersLoading
                ? darkMode
                  ? "bg-neutral-800 text-neutral-500"
                  : "bg-neutral-200 text-neutral-400"
                : darkMode
                  ? "bg-forfy-500/15 text-forfy-300 hover:bg-forfy-500/25"
                  : "bg-forfy-50 text-forfy-700 hover:bg-forfy-100"
            }`}
          >
            <RefreshCw
              className={`w-4 h-4 ${
                loading || usersLoading ? "animate-spin" : ""
              }`}
            />
            Aggiorna
          </button>
        </div>

        {error && (
          <div
            className={`mb-4 p-3 rounded-lg text-sm ${
              darkMode ? "bg-red-900/30 text-red-200" : "bg-red-50 text-red-700"
            }`}
          >
            {error}
          </div>
        )}

        {!data && !error && (
          <div
            className={`text-sm ${
              darkMode ? "text-neutral-400" : "text-neutral-500"
            }`}
          >
            Caricamento metriche...
          </div>
        )}

        {data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <KpiCard
                icon={Users}
                label="Utenti totali"
                value={fmtInt(data.usersTotal)}
                darkMode={darkMode}
              />
              <KpiCard
                icon={Activity}
                label="Attivi (7gg)"
                value={fmtInt(data.usersActive7d)}
                hint={
                  data.usersTotal > 0
                    ? `${((data.usersActive7d / data.usersTotal) * 100).toFixed(0)}% del totale`
                    : undefined
                }
                darkMode={darkMode}
              />
              <KpiCard
                icon={FileStack}
                label="Documenti"
                value={fmtInt(data.documentsTotal)}
                hint="fatture + clienti + timesheet + F24 + contributi"
                darkMode={darkMode}
              />
              <KpiCard
                icon={Bot}
                label="Richieste Olli"
                value={fmtInt(data.olliRequestsTotal)}
                darkMode={darkMode}
              />
              <KpiCard
                icon={DollarSign}
                label="Costo Olli (mese)"
                value={fmtUSD(data.costUSDMonth)}
                hint="stima basata su estimatedCostUSD"
                darkMode={darkMode}
              />
              <KpiCard
                icon={Mail}
                label="Email inviate (mese)"
                value={fmtInt(data.emailsThisMonth)}
                darkMode={darkMode}
              />
            </div>

            {/* Progress bars */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <ProgressBar
                label="Storage stimato"
                current={storageEstimateKB}
                max={STORAGE_LIMIT_KB}
                unit="KB"
                darkMode={darkMode}
              />
              <ProgressBar
                label="Email mensili"
                current={data.emailsThisMonth}
                max={EMAIL_MONTHLY_LIMIT}
                unit=""
                darkMode={darkMode}
              />
            </div>

            {/* Trend chart */}
            <TrendChart data={data.trend} darkMode={darkMode} />

            {/* Richieste di accesso */}
            <AccessRequestsSection darkMode={darkMode} />

            {/* Whitelist (accesso Pro senza limiti) */}
            <AccessiApprovatiSection darkMode={darkMode} adminEmail={user?.email} />
            <WhitelistSection darkMode={darkMode} adminEmail={user?.email} />

            {/* Feedback utenti */}
            <FeedbackAdminSection darkMode={darkMode} />

            {/* Tabella utenti */}
            <div className="mt-4">
              {usersError && (
                <div
                  className={`mb-2 p-3 rounded-lg text-sm ${
                    darkMode
                      ? "bg-red-900/30 text-red-200"
                      : "bg-red-50 text-red-700"
                  }`}
                >
                  {usersError}
                </div>
              )}
              <UsersTable
                data={usersData}
                loading={usersLoading}
                darkMode={darkMode}
              />
            </div>

            {/* Azioni manutenzione */}
            <div
              className={`rounded-xl border p-4 mt-4 ${
                darkMode
                  ? "bg-neutral-900 border-neutral-800"
                  : "bg-white border-neutral-200"
              }`}
            >
              <h3
                className={`text-sm font-semibold mb-2 ${
                  darkMode ? "text-neutral-200" : "text-neutral-800"
                }`}
              >
                Manutenzione
              </h3>
              <p
                className={`text-xs mb-3 ${
                  darkMode ? "text-neutral-400" : "text-neutral-500"
                }`}
              >
                Rilancia la sincronizzazione dei ruoli su tutti gli utenti
                Firebase Auth (idempotente).
              </p>
              <button
                onClick={runMigration}
                disabled={migrating}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  migrating
                    ? darkMode
                      ? "bg-neutral-800 text-neutral-500"
                      : "bg-neutral-200 text-neutral-400"
                    : darkMode
                      ? "bg-neutral-800 text-neutral-200 hover:bg-neutral-700"
                      : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                }`}
              >
                <UserCog
                  className={`w-4 h-4 ${migrating ? "animate-pulse" : ""}`}
                />
                {migrating ? "Migrazione in corso..." : "Migra ruoli utenti"}
              </button>
              {migrateResult && (
                <div
                  className={`mt-3 text-xs p-2 rounded ${
                    migrateResult.ok
                      ? darkMode
                        ? "bg-green-900/30 text-green-200"
                        : "bg-green-50 text-green-700"
                      : darkMode
                        ? "bg-red-900/30 text-red-200"
                        : "bg-red-50 text-red-700"
                  }`}
                >
                  {migrateResult.ok
                    ? `Migrazione completata: ${migrateResult.processed} utenti processati (${migrateResult.adminsSet} admin, ${migrateResult.usersSet} user).`
                    : `Errore: ${migrateResult.message}`}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
